import React, { useEffect } from 'react';
// import { makeStyles, lighten } from '@mui/styles';
import { makeStyles } from '@mui/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import clsx from 'clsx';
import MuiToolbar from '@mui/material/Toolbar';
import TableHead from '@mui/material/TableHead';
import TableSortLabel from '@mui/material/TableSortLabel';
import Checkbox from '@mui/material/Checkbox';
import { CircularProgress, Tooltip, Typography } from '@mui/material';
import { ThemeContext } from '../devices/AssociazioneMassiva';
import { useContext } from 'react';
import { getStatusIcon } from '../../Utilities/helperFunctions';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    marginTop: '1em',
    marginBottom: '1em',
  },
  message: {
    margin: '2em 0',
    textAlign: 'center'
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}));

const useToolbarStyles = makeStyles((theme) => ({
  toolbar: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === 'light'
      ? {
        color: theme.palette.secondary.main,
        // backgroundColor: lighten(theme.palette.secondary.light, 0.85),
      }
      : {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.secondary.dark,
      },
  title: {
    flex: '1 1 100%',
  },
}));


const headCells = [
  { id: 'serial_number_tim', numeric: false, disablePadding: true, label: 'Serial Number TIM' },
  { id: 'cod_nmu', numeric: false, disablePadding: false, label: 'COD NMU' },
  { id: 'status', numeric: false, disablePadding: false, label: 'Stato' },
  { id: 'cod_terr_sap', numeric: false, disablePadding: false, label: 'Cod Sap Magazzino' },
  { id: 'flag', numeric: false, disablePadding: true, label: 'Guasto al Primo utilizzo' },
  { id: 'result', numeric: false, disablePadding: false, label: 'Result' },
];

/**
 * Renders a custom MUI table that allows selection of devices.
 * 
 * This component consumes the context state from its parent component using useContext
 *
 * @param {array} title The table title
 * @param {array} executeUpdate If true then execute update
 */
function SelectableRowsTable() {
  const classes = useStyles();
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('serial_number_tim');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const {
    filters,
    NMUQuantities,
    isValidating,
    enableSorting,
    setEnableSorting,
    rows,
    setHasChanged,
    updateRow,
    selected,
    handleSelectRow,
  } = useContext(ThemeContext);
  const classesToolbar = useToolbarStyles();

  useEffect(() => {
    if (isValidating) setPage(0);
  }, [isValidating])

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setEnableSorting(true);
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler = (property) => (event) => {
    handleRequestSort(event, property);
  };

  const handleSelectGuastoPrimoUtilizzo = (e, row) => {
    let newRow = JSON.parse(JSON.stringify(row));
    
    if (e.target.checked) newRow.device.reverse = { codice: '1', name: 'Guasto al Primo utilizzo' }
    else delete newRow.device.reverse;

    if ((newRow.device.reverse && !row.device.reverse) || (!newRow.device.reverse && row.device.reverse))
      newRow.device.hasChanged = true;
    
    if (e.target.checked)
      handleSelectRow(e, newRow, true)
    if (!e.target.checked)
      handleSelectRow(e, newRow, false, true)
    setHasChanged(true);
  }

  const handleChangePage = (e, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  let emptyRows;
  if (rows) emptyRows = rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  return (
    <div>
      <TableContainer>
        <Table
          aria-labelledby="tableTitle"
          size="medium"
          aria-label="enhanced table"
          dense="true"
          disableSelectionOnClick
        >
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox"> </TableCell>
              {headCells.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  align={headCell.numeric ? 'right' : 'left'}
                  padding={headCell.disablePadding ? 'none' : 'normal'}
                  sortDirection={orderBy === headCell.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={createSortHandler(headCell.id)}
                  >
                    {headCell.label}
                    {orderBy === headCell.id ? (
                      <span className={classes.visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </span>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows && NMUQuantities && rows.length > 0 && sortAndFilter(rows, getComparator(order, orderBy), enableSorting, filters)
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => {
                const isItemSelected = isSelected(row.device.id);
                const labelId = `enhanced-table-checkbox-${index}`;
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.device.serial_number_tim}
                    selected={isItemSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        onChange={(event) => handleSelectRow(event, row)}
                        checked={isItemSelected}
                        inputProps={{ 'aria-labelledby': labelId }}
                        disabled={!isItemSelected && NMUQuantities.find(nmu => nmu.values.some(val => val == row.device.cod_nmu)).selected == NMUQuantities.find(nmu => nmu.values.some(val => val == row.device.cod_nmu)).quantity}
                      />
                    </TableCell>
                    <TableCell component="th" id={labelId} scope="row" padding="none">
                      {row.device.serial_number_tim}
                    </TableCell>
                    <TableCell align="left">{row.device.cod_nmu}</TableCell>
                    <TableCell align="left">{row.device.status?.descrizione}</TableCell>
                    <TableCell align="left">{row.device.cod_terr_sap}</TableCell>
                    <TableCell>
                      <Checkbox
                        inputProps={{ 'aria-label': 'Guasto al Primo utilizzo' }}
                        // disabled={!isItemSelected}
                        checked={row.device.reverse?.codice === '1'}
                        onChange={(event) => handleSelectGuastoPrimoUtilizzo(event, row)}
                      />
                    </TableCell>
                    <TableCell align="left">
                      <Tooltip title={<Typography variant="body2">{row.status?.message}</Typography>}>
                        <div style={{ marginLeft: '10px' }}>
                          {row.loading ? <CircularProgress size={14} /> : row.status ? getStatusIcon(row.status.type) : null}
                        </div>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={6} style={{ textAlign: 'center' }}>{ rows == 0 && "No apparati" }</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={rows?.length || 0}
        rowsPerPage={rowsPerPage}
        labelRowsPerPage={"Righe per pagina"}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div >
  );
}

function descendingComparator(a, b, orderBy) {
  let aValue = a[orderBy]
  let bValue = b[orderBy];

  if (aValue && bValue) {
    if (orderBy === 'status') {
      aValue = aValue.descrizione;
      bValue = bValue.descrizione;
    }

    if (bValue < aValue) {
      return -1;
    }
    if (bValue > aValue) {
      return 1;
    }
  }
  else return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a.device, b.device, orderBy)
    : (a, b) => -descendingComparator(a.device, b.device, orderBy);
}

function sortAndFilter(array, comparator, enableSorting, filters) {
  let filteredArray = array;
  if (filters) {
    if (filters.serial_number_tim) {
      let serials = filters.serial_number_tim.split(',');
      filteredArray = filteredArray.filter(row => serials.includes(row.device.serial_number_tim));
    }
    if (filters.cod_terr_sap) {
      filteredArray = filteredArray.filter(row => row.device.cod_terr_sap.includes(filters.cod_terr_sap));
    }
    if (filters.cod_nmu) {
      filteredArray = filteredArray.filter(row => row.device.cod_nmu.toString().includes(filters.cod_nmu.toString()));
    }
  }
  if (enableSorting) {
    const stabilizedThis = filteredArray.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  } else return filteredArray;
}

export default SelectableRowsTable;